import { Clock } from "lucide-react";
import { LUNCH_SPECIAL, MENU, SIDES, DRINKS } from "../data/site";
import { useContent } from "@/context/ContentContext";
import { Reveal } from "./Reveal";

const tid = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

const ItemRow = ({ item, prefix = "menu" }) => (
  <li
    data-testid={`${prefix}-item-${tid(item.name)}`}
    className="flex items-baseline gap-3 py-2.5 px-3 -mx-3 rounded-xl hover:bg-beige/60 transition-colors"
  >
    <div className="min-w-0">
      <span className="font-body font-medium text-charcoal">{item.name}</span>
      {item.desc && (
        <span className="block font-body text-xs text-charcoal/50 italic">{item.desc}</span>
      )}
    </div>
    <span className="flex-1 border-b border-dotted border-charcoal/25 translate-y-[-3px]" />
    <span className="font-heading font-semibold text-burgundy whitespace-nowrap">{item.price}</span>
  </li>
);

export const MenuSection = () => {
  const { content } = useContent();
  const lunchSpecial = content?.lunchSpecial || LUNCH_SPECIAL;
  const menu = content?.menu?.length ? content.menu : MENU;
  const sides = content?.sides?.length ? content.sides : SIDES;
  const drinks = content?.drinks?.length ? content.drinks : DRINKS;
  return (
    <section id="menu" data-testid="menu-section" className="bg-cream py-24 md:py-32 relative">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-16">
          <Reveal>
            <span className="font-heading text-gold-dark uppercase tracking-[0.3em] text-sm">
              — Fresh Daily
            </span>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-4 font-heading text-4xl md:text-5xl font-semibold text-charcoal">
              Our <span className="font-script text-burgundy text-5xl md:text-6xl">Menu</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-4 font-body text-charcoal/60 max-w-xl mx-auto">
              Soul food with a Caribbean flair — all made from scratch.
            </p>
          </Reveal>
        </div>

        {/* Lunch Special */}
        <Reveal
          data-testid="lunch-special"
          className="mb-8 bg-white rounded-3xl border-2 border-gold/60 shadow-[0_8px_30px_rgba(168,141,88,0.12)] overflow-hidden"
        >
          <div className="grid md:grid-cols-3">
            <div className="bg-burgundy p-8 md:p-9 flex flex-col justify-center">
              <span className="font-body text-xs uppercase tracking-[0.25em] text-gold-light">
                Lunch Special
              </span>
              <h3 className="mt-2 font-script text-gold-light text-4xl md:text-5xl leading-none">
                Midday Deal
              </h3>
              <div className="mt-5 space-y-2 text-cream/90 font-body text-sm">
                <p className="flex items-center gap-2">
                  <Clock size={15} className="text-gold" /> {lunchSpecial.time}
                </p>
                <p className="pl-6">{lunchSpecial.days}</p>
                <p className="pl-6 italic text-cream/70">{lunchSpecial.note}</p>
              </div>
            </div>
            <ul className="md:col-span-2 p-7 md:p-9 grid sm:grid-cols-2 gap-x-8 content-center">
              {lunchSpecial.items.map((item) => (
                <ItemRow key={item.name} item={item} prefix="lunch" />
              ))}
            </ul>
          </div>
        </Reveal>

        {/* Entrée categories */}
        <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
          {menu.map((cat, ci) => (
            <Reveal
              key={cat.category}
              delay={ci * 0.05}
              className="bg-white rounded-3xl p-7 md:p-9 border border-beige shadow-[0_4px_20px_rgba(122,28,34,0.05)]"
            >
              <div className="flex items-baseline justify-between border-b border-gold/30 pb-4 mb-5">
                <h3 className="font-heading text-2xl md:text-3xl font-semibold text-burgundy">
                  {cat.category}
                </h3>
                <span className="font-body text-xs uppercase tracking-widest text-gold-dark">
                  {cat.note}
                </span>
              </div>
              <ul className="space-y-1">
                {cat.items.map((item) => (
                  <ItemRow key={item.name} item={item} prefix={tid(cat.category)} />
                ))}
              </ul>
            </Reveal>
          ))}
        </div>

        {/* Sides + Drinks */}
        <div className="grid md:grid-cols-2 gap-6 lg:gap-8 mt-6 lg:mt-8">
          {/* Sides */}
          <Reveal
            data-testid="sides-card"
            className="bg-white rounded-3xl p-7 md:p-9 border border-beige shadow-[0_4px_20px_rgba(122,28,34,0.05)]"
          >
            <div className="flex items-baseline justify-between border-b border-gold/30 pb-3 mb-2">
              <h3 className="font-heading text-2xl md:text-3xl font-semibold text-burgundy">Sides</h3>
              <div className="flex gap-6 font-body text-xs uppercase tracking-widest text-gold-dark">
                <span className="w-12 text-right">Small</span>
                <span className="w-12 text-right">Large</span>
              </div>
            </div>
            <ul>
              {sides.map((s) => (
                <li
                  key={s.name}
                  data-testid={`side-item-${tid(s.name)}`}
                  className="flex items-center gap-3 py-2.5 px-3 -mx-3 rounded-xl hover:bg-beige/60 transition-colors"
                >
                  <span className="font-body font-medium text-charcoal flex-1">{s.name}</span>
                  <span className="w-12 text-right font-heading font-semibold text-charcoal/70">{s.small}</span>
                  <span className="w-12 text-right font-heading font-semibold text-burgundy">{s.large}</span>
                </li>
              ))}
            </ul>
          </Reveal>

          {/* Drinks */}
          <Reveal
            data-testid="drinks-card"
            className="bg-burgundy rounded-3xl p-7 md:p-9 text-cream shadow-[0_8px_30px_rgba(122,28,34,0.15)]"
          >
            <div className="border-b border-gold/40 pb-4 mb-5">
              <h3 className="font-heading text-2xl md:text-3xl font-semibold text-cream">Drinks</h3>
              <span className="font-script text-gold-light text-2xl">Something to sip</span>
            </div>
            <ul className="grid sm:grid-cols-2 gap-x-8">
              {drinks.map((d) => (
                <li
                  key={d.name}
                  data-testid={`drink-item-${tid(d.name)}`}
                  className="flex items-baseline gap-2 py-1.5"
                >
                  <span className="font-body text-cream/90">{d.name}</span>
                  <span className="flex-1 border-b border-dotted border-cream/25 translate-y-[-3px]" />
                  <span className="font-heading font-semibold text-gold-light whitespace-nowrap">{d.price}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
